import {
	QueryObserverOptions,
	useQuery,
	useQueryClient,
	useMutation,
} from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { useEffect, useMemo } from 'react'
import { InboxService } from '../../lib/api/inbox.services'
import {
	DefaultInboxParamsProps,
	GetInboxDataResponse,
	GetInboxParamsProps,
	GetUnreadInboxCountDataResponse,
	UpdateStatusInboxDataProps,
	InboxItem,
	MultiActionInboxDataProps,
	DeleteInboxParamsProps,
} from '../../lib/api/types/inbox.interfaces'
import { handleApiError } from '../../lib/utils/handleApiError'
import toastCaller from '../../lib/utils/toastCaller'

export const useInbox = ({ offset = 0,
		limit = 10,
		columnFilters,
		...params
	}: GetInboxParamsProps,
	options?: Partial<ReturnType<typeof useQuery<GetInboxDataResponse>>>
) => {
	const { isAuthenticated } = useAuth()
	const apiParams = useMemo(() => {
		const result: any = { ...params }

		if (columnFilters) {
			columnFilters.forEach((filter) => {
				switch (filter.id) {
					case 'createdAt':
						if (
							filter.value &&
							typeof filter.value === 'object' &&
							'startValue' in filter.value
						) {
							if (filter.value.startValue)
								result.startDate = filter.value.startValue
							if (filter.value.endValue) result.endDate = filter.value.endValue
						}
						break
					case 'category':
						if (filter.value) result.category = filter.value
						break
					case 'from':
						if (filter.value) result.fromId = filter.value
						break
					case 'isPinned':
						if (filter.value !== undefined) result.isPinned = filter.value
						break
				}
			})
		}

		return result
	}, [columnFilters, params])
	const queryClient = useQueryClient()

	const pageSize = limit * 3
	const forcedOffset = Math.floor(offset / pageSize) * pageSize

	const queryKey = ['inbox', offset, limit, JSON.stringify(apiParams)]

	const pageKey = [
		'inbox-page',
		forcedOffset,
		pageSize,
		JSON.stringify(apiParams),
	]

	const cachedPage = queryClient.getQueryData<GetInboxDataResponse>(pageKey)

	const initialData =
		cachedPage && offset !== 0
			? {
					...cachedPage,
					data: {
						...cachedPage.data,
						entries: cachedPage.data.entries.slice(
							offset - forcedOffset,
							offset - forcedOffset + limit
						),
						pagination: {
							...cachedPage.data.pagination,
							offset,
							limit,
						},
					},
			  }
			: undefined

	const queryFn = async () => {
		if (cachedPage && offset !== 0) {
			return {
				...cachedPage,
				data: {
					...cachedPage.data,
					entries: cachedPage.data.entries.slice(
						offset - forcedOffset,
						offset - forcedOffset + limit
					),
					pagination: {
						...cachedPage.data.pagination,
						offset,
						limit,
					},
				},
			}
		}
		const fresh = await InboxService.get({ offset: forcedOffset,
			limit: pageSize,
			...apiParams,
		})
		queryClient.setQueryData(pageKey, fresh)
		return {
			...fresh,
			data: {
				...fresh.data,
				entries: fresh?.data?.entries?.slice(
					offset - forcedOffset,
					offset - forcedOffset + limit
				),
				pagination: {
					...fresh.data.pagination,
					offset,
					limit,
				},
			},
		}
	}

	const queryResult = useQuery<GetInboxDataResponse>({
		queryKey,
		enabled: isAuthenticated,
		queryFn,
		initialData,
		placeholderData: () => initialData,
		...options,
	})

	const hardFetch = async (currentOffset = offset) => {
		const groupOffset = Math.floor(currentOffset / pageSize) * pageSize
		const fresh = await InboxService.get({ offset: groupOffset,
			limit: pageSize,
			...apiParams,
		})
		const groupKey = [
			'inbox-page',
			groupOffset,
			pageSize,
			JSON.stringify(apiParams),
		]
		queryClient.setQueryData(groupKey, fresh)
		const view = {
			...fresh,
			data: {
				...fresh.data,
				entries: fresh?.data?.entries?.slice(
					currentOffset - groupOffset,
					currentOffset - groupOffset + limit
				),
				pagination: {
					...fresh.data.pagination,
					offset: currentOffset,
					limit,
				},
			},
		}
		queryClient.setQueryData(queryKey, view)
		return view
	}

	const updateItems = async ({
		forceRefetch = false,
		offset: newOffset,
	}: { forceRefetch?: boolean; offset?: number } = {}) => {
		const currentOffset = typeof newOffset === 'number' ? newOffset : offset
		const currentGroupOffset = Math.floor(currentOffset / pageSize) * pageSize
		const currentPageKey = [
			'inbox-page',
			currentGroupOffset,
			pageSize,
			JSON.stringify(apiParams),
		]

		if (forceRefetch) {
			return queryClient.fetchQuery({
				queryKey,
				queryFn: async () => {
					const fresh = await InboxService.get({ offset: currentGroupOffset,
						limit: pageSize,
						...apiParams,
					})
					queryClient.setQueryData(currentPageKey, fresh)
					return {
						...fresh,
						data: {
							...fresh.data,
							entries: fresh?.data?.entries?.slice(
								currentOffset - currentGroupOffset,
								currentOffset - currentGroupOffset + limit
							),
							pagination: {
								...fresh.data.pagination,
								offset: currentOffset,
								limit,
							},
						},
					}
				},
				staleTime: 0,
			})
		}
		return queryClient.fetchQuery({
			queryKey,
			queryFn: () => queryFn(),
			staleTime: 0,
		})
	}

	useEffect(() => {
		// Always refresh from server on page entry if we already have cached page data
		// to ensure fresh content while preserving instant UI from cache.
		if (!isAuthenticated || !cachedPage) return
		;(async () => {
			try {
				await hardFetch(offset)
			} catch (_err) {}
		})()
	}, [])

	return { ...queryResult, updateItems }
}

export const useUnreadInboxCount = (params: GetInboxParamsProps,
	options?: Partial<QueryObserverOptions<GetUnreadInboxCountDataResponse>>
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetUnreadInboxCountDataResponse>({
		queryKey: ['inboxUnreadCount'],
		queryFn: () => InboxService.getUnreadNumber(),
		enabled: isAuthenticated,
		staleTime: 5000,
		retry: 1,
		...options,
	})
}

export const useInboxUnread = ({ offset = 0,
		limit = 10,
		columnFilters,
		...params
	}: GetInboxParamsProps,
	options?: Partial<ReturnType<typeof useQuery<GetInboxDataResponse>>>
) => {
	const { isAuthenticated } = useAuth()
	const apiParams = useMemo(() => {
		const result: any = { ...params, isRead: false }

		if (columnFilters) {
			columnFilters.forEach((filter) => {
				switch (filter.id) {
					case 'createdAt':
						if (
							filter.value &&
							typeof filter.value === 'object' &&
							'startValue' in filter.value
						) {
							if (filter.value.startValue)
								result.startDate = filter.value.startValue
							if (filter.value.endValue) result.endDate = filter.value.endValue
						}
						break
					case 'category':
						if (filter.value) result.category = filter.value
						break
					case 'from':
						if (filter.value) result.fromId = filter.value
						break
					case 'isPinned':
						if (filter.value !== undefined) result.isPinned = filter.value
						break
				}
			})
		}

		return result
	}, [columnFilters, params])

	const queryClient = useQueryClient()

	const pageSize = limit * 3
	const forcedOffset = Math.floor(offset / pageSize) * pageSize

	const queryKey = [
		'inboxUnread',
		offset,
		limit,
		JSON.stringify(apiParams),
	]

	const pageKey = [
		'inboxUnread-page',
		forcedOffset,
		pageSize,
		JSON.stringify(apiParams),
	]

	const cachedPage = queryClient.getQueryData<GetInboxDataResponse>(pageKey)

	const initialData =
		cachedPage && offset !== 0
			? {
					...cachedPage,
					data: {
						...cachedPage.data,
						entries: cachedPage.data.entries.slice(
							offset - forcedOffset,
							offset - forcedOffset + limit
						),
						pagination: {
							...cachedPage.data.pagination,
							offset,
							limit,
						},
					},
			  }
			: undefined

	const queryFn = async () => {
		if (cachedPage && offset !== 0) {
			return {
				...cachedPage,
				data: {
					...cachedPage.data,
					entries: cachedPage.data.entries.slice(
						offset - forcedOffset,
						offset - forcedOffset + limit
					),
					pagination: {
						...cachedPage.data.pagination,
						offset,
						limit,
					},
				},
			}
		}
		const fresh = await InboxService.get({ offset: forcedOffset,
			limit: pageSize,
			...apiParams,
		})
		queryClient.setQueryData(pageKey, fresh)
		return {
			...fresh,
			data: {
				...fresh.data,
				entries: fresh?.data?.entries?.slice(
					offset - forcedOffset,
					offset - forcedOffset + limit
				),
				pagination: {
					...fresh.data.pagination,
					offset,
					limit,
				},
			},
		}
	}

	const queryResult = useQuery<GetInboxDataResponse>({
		queryKey,
		enabled: isAuthenticated,
		queryFn,
		initialData,
		placeholderData: () => initialData,
		...options,
	})

	const hardFetch = async (currentOffset = offset) => {
		const groupOffset = Math.floor(currentOffset / pageSize) * pageSize
		const fresh = await InboxService.get({ offset: groupOffset,
			limit: pageSize,
			...apiParams,
		})
		const groupKey = [
			'inboxUnread-page',
			groupOffset,
			pageSize,
			JSON.stringify(apiParams),
		]
		queryClient.setQueryData(groupKey, fresh)
		const view = {
			...fresh,
			data: {
				...fresh.data,
				entries: fresh?.data?.entries?.slice(
					currentOffset - groupOffset,
					currentOffset - groupOffset + limit
				),
				pagination: {
					...fresh.data.pagination,
					offset: currentOffset,
					limit,
				},
			},
		}
		queryClient.setQueryData(queryKey, view)
		return view
	}

	const updateItems = async ({
		forceRefetch = false,
		offset: newOffset,
	}: { forceRefetch?: boolean; offset?: number } = {}) => {
		const currentOffset = typeof newOffset === 'number' ? newOffset : offset
		const currentGroupOffset = Math.floor(currentOffset / pageSize) * pageSize
		const currentPageKey = [
			'inboxUnread-page',
			currentGroupOffset,
			pageSize,
			JSON.stringify(apiParams),
		]

		if (forceRefetch) {
			return queryClient.fetchQuery({
				queryKey,
				queryFn: async () => {
					const fresh = await InboxService.get({ offset: currentGroupOffset,
						limit: pageSize,
						...apiParams,
					})
					queryClient.setQueryData(currentPageKey, fresh)
					return {
						...fresh,
						data: {
							...fresh.data,
							entries: fresh?.data?.entries?.slice(
								currentOffset - currentGroupOffset,
								currentOffset - currentGroupOffset + limit
							),
							pagination: {
								...fresh.data.pagination,
								offset: currentOffset,
								limit,
							},
						},
					}
				},
				staleTime: 0,
			})
		}
		return queryClient.fetchQuery({
			queryKey,
			queryFn: () => queryFn(),
			staleTime: 0,
		})
	}

	useEffect(() => {
		// Always refresh from server on page entry if we already have cached page data
		// to ensure fresh content while preserving instant UI from cache.
		if (!isAuthenticated || !cachedPage) return
		;(async () => {
			try {
				await hardFetch(offset)
			} catch (_err) {}
		})()
	}, [])

	return { ...queryResult, updateItems }
}

// single actions
export const useToggleInboxRead = () => {
	const { updateItemStatus, updateUnreadCount } = useUpdateInboxCache()

	const mutation = useMutation({
		mutationKey: ['inboxMarkRead'],
		mutationFn: ({ id, value }: UpdateStatusInboxDataProps) =>
			InboxService.toggleRead({ id, value }),
		onError: (error) => handleApiError(error),
		onSuccess: (_, { id, value }) => {
			updateItemStatus([id], { isRead: value })
			updateUnreadCount(value, 1)
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
export const useToggleInboxPinned = () => {
	const { updateItemStatus } = useUpdateInboxCache()

	const mutation = useMutation({
		mutationKey: ['inboxMarkFavorite'],
		mutationFn: ({ id, value }: UpdateStatusInboxDataProps) =>
			InboxService.togglePinned({ id, value }),
		onError: (error) => handleApiError(error),
		onSuccess: (_, { id, value }) => {
			updateItemStatus([id], { isPinned: value })
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
export const useDeleteInboxItem = () => {
	const queryClient = useQueryClient()

	const invalidateInbox = () => {
		queryClient.invalidateQueries({ queryKey: ['inbox'], exact: false })
		queryClient.invalidateQueries({
			queryKey: ['inboxUnreadCount'],
			exact: false,
		})
	}

	const mutation = useMutation({
		mutationKey: ['inboxDeleteSingle'],
		mutationFn: ({ id }: { id: string }) =>
			InboxService.delete({ id}),
		onError: (error) => handleApiError(error),
		onSuccess: (_, { id }) => {
			invalidateInbox()

			toastCaller({
				type: 'success',
				message: 'Notification deleted successfully!',
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
// ---

// multi actions (need to update)
export const useMultiToggleInboxRead = () => {
	const { updateItemStatus, updateUnreadCount } = useUpdateInboxCache()

	const mutation = useMutation({
		mutationKey: ['inboxMarkRead'],
		mutationFn: ({ data }: MultiActionInboxDataProps) =>
			InboxService.multiToggleRead({ data }),
		onError: (error) => handleApiError(error),
		onSuccess: (_, { data: { value, notifications = [] } }) => {
			if (notifications && Array.isArray(notifications)) {
				updateItemStatus(notifications, { isRead: value })

				if (value === false) {
					updateUnreadCount(false, notifications?.length)
				} else {
					updateUnreadCount(true, notifications?.length)
				}
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
export const useMultiToggleInboxPinned = () => {
	const { updateItemStatus } = useUpdateInboxCache()

	const mutation = useMutation({
		mutationKey: ['inboxMarkFavorite'],
		mutationFn: ({ data }: MultiActionInboxDataProps) =>
			InboxService.multiToggleFavorite({ data }),
		onError: (error) => handleApiError(error),
		onSuccess: (_, { data: { value, notifications } }) => {
			if (notifications && Array.isArray(notifications)) {
				updateItemStatus(notifications, { isPinned: value })
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
export const useMultiDeleteInboxItems = () => {
	const queryClient = useQueryClient()

	const invalidateInbox = () => {
		queryClient.invalidateQueries({ queryKey: ['inbox'], exact: false })
		queryClient.invalidateQueries({
			queryKey: ['inboxUnreadCount'],
			exact: false,
		})
	}

	const mutation = useMutation({
		mutationKey: ['inboxDeleteMultiple'],
		mutationFn: ({ ids}: DeleteInboxParamsProps) =>
			InboxService.deleteMultiple({ ids}),
		onError: (error) => handleApiError(error),
		onSuccess: (_, { ids }) => {
			invalidateInbox()

			toastCaller({
				type: 'success',
				message: `${ids?.length} notifications deleted successfully!`,
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

// ---

// update cache hook
export const useUpdateInboxCache = () => {
	const queryClient = useQueryClient()

	const updateItemStatus = (ids: string[], updates: Partial<InboxItem>) => {
		queryClient.setQueriesData(
			{ queryKey: ['inbox'], exact: false },
			(oldData: GetInboxDataResponse | undefined) => {
				if (!oldData) return oldData

				return {
					...oldData,
					data: {
						...oldData.data,
						entries: oldData.data.entries.map((entry) =>
							ids.includes(entry.id) ? { ...entry, ...updates } : entry
						),
					},
				}
			}
		)

		queryClient.setQueriesData(
			{ queryKey: ['inbox-page'], exact: false },
			(oldData: GetInboxDataResponse | undefined) => {
				if (!oldData) return oldData

				return {
					...oldData,
					data: {
						...oldData.data,
						entries: oldData.data.entries.map((entry) =>
							ids.includes(entry.id) ? { ...entry, ...updates } : entry
						),
					},
				}
			}
		)
	}

	const updateUnreadCount = (decrement: boolean, count: number = 1) => {
		queryClient.setQueriesData(
			{ queryKey: ['inboxUnreadCount'], exact: false },
			(oldData: GetUnreadInboxCountDataResponse | undefined) => {
				if (!oldData) return oldData

				const currentCount = oldData.data
				const newCount = decrement
					? Math.max(0, currentCount - count)
					: currentCount + count

				return {
					...oldData,
					data: newCount,
				}
			}
		)
	}

	return {
		updateItemStatus,
		updateUnreadCount,
	}
}
